/**
 * Pure routing predicates extracted from `router.tsx` so they're unit-testable
 * in isolation. The router is a JSX-heavy React component; the decisions
 * about WHICH layout to render for a given (venue-config, pathname, runtime)
 * are pure logic that historically lived inline and produced silent regressions
 * (Love Inn native app rendered the website mid-2026-09; see PR #184).
 *
 * Rules of thumb encoded here:
 *   - Native app ALWAYS gets app-mode. It's a native shell wrapping the SPA;
 *     rendering the website inside it is always wrong.
 *   - Browser can render the website when the venue explicitly opts in
 *     (websiteMode) or when a non-home slug matches a configured sitePage
 *     (Prospect's /account-deletion doesn't need websiteMode).
 */

import { pageForPath, type SitePagesConfig } from "./site-pages";

export type SitePagesRouteInput = {
  sitePages: SitePagesConfig | undefined | null;
  pathname: string;
  isNativeApp: boolean;
  websiteMode: boolean; // caller's already-computed websiteMode boolean (accounts for coming-soon etc.)
  /**
   * True inside the admin preview iframe: draft pages should render so
   * operators can build them before publishing. Caller passes the flag (kept
   * pure by not touching preview-overlay from here).
   */
  includeDrafts?: boolean;
};

/**
 * True when the router should hand pathname to <SitePagesSite>.
 *
 * Native app: NEVER. This is the load-bearing rule, Love Inn users were
 * seeing the website inside their app because this gate was missing.
 * Browser home ("/"): only when websiteMode is on (protects venues that
 * haven't opted their whole home over from having it hijacked by a config
 * entry with an empty slug).
 * Browser non-home: whenever the venue has that slug configured, supports
 * Prospect shipping a single /account-deletion page without switching its
 * whole home to sitePages.
 * Draft pages: hidden from public visitors (pageForPath filters), rendered
 * in the admin preview iframe when `includeDrafts` is true.
 */
export function shouldRenderSitePages(input: SitePagesRouteInput): boolean {
  if (input.isNativeApp) return false;
  if (!input.sitePages?.pages?.length) return false;
  const match = pageForPath(input.sitePages, input.pathname, { includeDrafts: input.includeDrafts });
  if (!match) return false;
  if (input.pathname === "/") return input.websiteMode;
  return true;
}

/**
 * When a browser visitor hits /terms or /privacy on a VENUE host, we default
 * to Assembly's platform-wide legal page BUT let the venue override by
 * authoring their own published site page with that slug.
 *
 * Returns true when the platform LegalPage should render, false when the
 * venue's own site page wins (the router falls through to normal venue
 * routing which resolves the sitePage).
 *
 * Tyler @ Toka 2026-09-19: created a Terms page in the builder + tapped
 * "Terms" in preview, got Assembly's legal page (with our contact email +
 * "Assembly Tickets Ltd" footer). Root cause: main.tsx short-circuited
 * `/privacy` + `/terms` to LegalPage BEFORE checking the venue's sitePages.
 *
 * Note: draft pages (published:false) DON'T qualify — Assembly's legal
 * page still shows publicly until the venue publishes their override.
 */
export function shouldRenderPlatformLegalPage(input: {
  slug: "terms" | "privacy";
  sitePages: SitePagesConfig | undefined | null;
}): boolean {
  const pages = input.sitePages?.pages;
  if (!Array.isArray(pages) || pages.length === 0) return true;
  const venueOverride = pages.find((p) => (p.slug ?? "") === input.slug && p.published !== false);
  return !venueOverride;
}
